import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MeetingService, AmplitudeService } from "../../core/services";
import { take } from "rxjs";
import { EmailModalComponent, InfoPopupComponent, MainWrapperComponent } from "@core/component";
import {
    faSquarePlus,
    faAngleRight, 
    faMinusSquare,
    faAngleDown,
    faAngleUp,
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";

@Component({
    selector: 'app-meetings',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        MainWrapperComponent,
        EmailModalComponent,
        InfoPopupComponent,
        FormsModule,
    ],
    templateUrl: './meetings.component.html',
    styleUrls: ['./meetings.component.scss'],  // fixed typo here
})
export class MeetingsComponent implements OnInit {
    @ViewChild('emailModal') private _emailModal!: EmailModalComponent;
    @ViewChild('infoModal') private _infoModal!: InfoPopupComponent;

    allMeetings: any[] = [];
    meetingBotId = '';
    collapsedDates: Set<string> = new Set();
    allExpanded = true;
    selectedEmails: string[] = [];

    // ── Pagination ────────────────────────────────────────────────────────
    private readonly _pageSize = 20;
    currentPage = 1;
    isLoading = false;
    hasNextPage = true;

    // ── Date range filter ──────────────────────────────────────────────
    filterFrom = '';
    filterTo = '';
    // draft values bound to the inputs before user hits Apply
    draftFrom = '';
    draftTo = '';

    dropsDown = 'down';
    opensRight = 'right';
    selectedSimpleCalendarTimeUpRight: any;
    meetingSortOrder: { [date: string]: boolean } = {};

    faMinusSquare = faMinusSquare;
    faSquarePlus = faSquarePlus;
    faAngleDown = faAngleDown;
    faAngleRight = faAngleRight;
    faAngleUp = faAngleUp;

    constructor(
        private readonly _meetingService: MeetingService,
        private readonly _route: ActivatedRoute,
        private readonly _router: Router,
        private readonly _amplitudeService: AmplitudeService
    ) { }

    ngOnInit(): void {
        // React to ALL query param changes — page, from, to
        this._route.queryParamMap.subscribe(params => {
            const page = Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1);
            this.filterFrom = params.get('from') ?? '';
            this.filterTo   = params.get('to')   ?? '';
            // Keep draft inputs in sync with URL
            this.draftFrom = this.filterFrom;
            this.draftTo   = this.filterTo;
            this._loadPage(page);
        });
    }

    private _loadPage(page: number): void {
        this.isLoading = true;
        const offset = (page - 1) * this._pageSize;
        this._meetingService
            .getCompletedOpportunityMeetings(this._pageSize, offset, this.filterFrom || null, this.filterTo || null)
            .pipe(take(1)).subscribe({
            next: (response) => {
                this.isLoading = false;
                const rows: any[] = Array.isArray(response) ? response : [];

                // Out-of-range page (e.g. ?page=9999) — redirect to page 1
                if (rows.length === 0 && page > 1) {
                    this._navigate(1);
                    return;
                }

                this.hasNextPage = rows.length === this._pageSize;
                this.currentPage = page;
                this.collapsedDates.clear();
                this.allExpanded = true;
                this.meetingSortOrder = {};
                this.allMeetings = this._buildGroups(rows);
            },
            error: () => { this.isLoading = false; }
        });
    }

    private _buildGroups(rows: any[]): any[] {
        const map: Record<string, any[]> = {};
        rows.forEach(meeting => {
            const date = meeting.start_time.split('T')[0];
            meeting.isMeetingHappened = !!meeting.actual_duration_minutes && !!meeting.distinct_speakers?.length;
            if (!map[date]) { map[date] = []; this.meetingSortOrder[date] = true; }
            map[date].push(meeting);
        });
        return Object.keys(map)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
            .map(date => ({
                date,
                meetings: map[date].sort((a: any, b: any) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
            }));
    }

    goToPrevPage(): void {
        if (this.currentPage > 1) this._navigate(this.currentPage - 1);
    }

    goToNextPage(): void {
        if (this.hasNextPage) this._navigate(this.currentPage + 1);
    }

    applyDateFilter(): void {
        this._navigate(1, this.draftFrom, this.draftTo);
    }

    clearDateFilter(): void {
        this.draftFrom = '';
        this.draftTo   = '';
        this._navigate(1, '', '');
    }

    get isFilterActive(): boolean {
        return !!(this.filterFrom || this.filterTo);
    }

    private _navigate(page: number, from = this.filterFrom, to = this.filterTo): void {
        this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {
                page,
                from: from || null,
                to:   to   || null,
            },
            queryParamsHandling: 'merge',
        });
    }

    isCollapsed(date: string): boolean {
        return this.collapsedDates.has(date);
    }

    toggleCollapse(date: string): void {
        if (this.collapsedDates.has(date)) {
            this.collapsedDates.delete(date);
        } else {
            this.collapsedDates.add(date);
        }
    }

    toggleExpandCollapse(): void {
        if (this.allExpanded) {
            this.collapsedDates = new Set(this.allMeetings.map(m => m.date));
        } else {
            this.collapsedDates.clear();
        }
        this.allExpanded = !this.allExpanded;
    }

    formatDuration(minutes: number): string {
        if (!minutes) return 'N/A';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        return hours > 0 ? `${hours}h ${mins}` : `${mins}`;
    }

    formatDurationMinutes(minutes: number): string {
        if (!minutes) return 'N/A';
        return `${Math.round(minutes)}'`;
    }

    goToOpportunity(meeting: any): void {
        this._amplitudeService.trackEvent('my_past_meeting_view', {
            meeting_id: meeting.bot_id,
            user_id: this._getUserId(),
            timestamp: new Date().toISOString(),
        });

        if (meeting.opportunity_id && meeting.isMeetingHappened) {
            localStorage.setItem('OPPORTUNITY_NAME', meeting.opportunity_name);
            this._router.navigate([`/opportunity-detail/${meeting.opportunity_id}`, meeting.bot_id]);
        } else {
            this._infoModal.open();
        }
    }

    emailMeetingPopup(meeting: any, event: Event): void {
        event.stopPropagation();

        this._amplitudeService.trackEvent('my_past_meeting_click_notes', {
            meeting_id: meeting.bot_id,
            section: 'notes',
            user_id: this._getUserId(),
        });

        if (meeting.isMeetingHappened) {
            this.meetingBotId = meeting.bot_id;
            this.selectedEmails = meeting.attendee_emails ?? [''];
            this._emailModal.open();
        } else {
            this._infoModal.open();
        }
    }

    trackFollowUpEdit(meeting: any, changes: string): void {
        this._amplitudeService.trackEvent('my_past_meeting_edit_followups', {
            meeting_id: meeting.bot_id,
            changes,
            user_id: this._getUserId(),
        });
    }

    toggleMeetingSort(date: string): void {
        this.meetingSortOrder[date] = !this.meetingSortOrder[date];
        const group = this.allMeetings.find(g => g.date === date);
        if (group) {
            group.meetings.sort((a: any, b: any) => {
                const diff = new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
                return this.meetingSortOrder[date] ? -diff : diff;
            });
        }
    }

    private _getUserId(): string {
        const userInfo = sessionStorage.getItem('userInfo');
        if (!userInfo) return 'anonymous';

        try {
            const parsed = JSON.parse(userInfo);
            return parsed.user?.id || parsed.id || 'anonymous';
        } catch {
            return 'anonymous';
        }
    }
}
